import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Copy, Eye, MessageSquare, X } from 'lucide-react';
import { fetchWhatsAppDeliveryLogs } from '../../services/MasterAdmin/whatsappHubService';
import type {
  WhatsAppDeliveryLog,
  WhatsAppDeliveryStatusCounts,
} from '../../models/MasterAdmin/whatsappHub';
import WhatsAppPanelShell from './WhatsAppPanelShell';

const SOURCE_LABELS: Record<string, string> = {
  transactional: 'Auto (backend)',
  ivr: 'IVR',
  campaign: 'Campaign',
  campaign_automated: 'Campaign (automated)',
  retargeting: 'Retargeting',
  inbox: 'Inbox',
};

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200',
  read: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200',
};

const EMPTY_COUNTS: WhatsAppDeliveryStatusCounts = {
  queued: 0,
  sent: 0,
  delivered: 0,
  read: 0,
  failed: 0,
  total: 0,
};

function StatusBadge({ status }: { status: string }) {
  const key = status.toLowerCase();
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[key] || STATUS_COLORS.queued}`}
    >
      {status}
    </span>
  );
}

function RecipientTypeBadge({ recipientType }: { recipientType?: string | null }) {
  const type = String(recipientType || 'user').toLowerCase();
  const label =
    type === 'vendor' || type === 'doctor'
      ? 'Doctor / Vendor'
      : type.charAt(0).toUpperCase() + type.slice(1);
  const color =
    type === 'vendor' || type === 'doctor'
      ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300'
      : type === 'driver' || type === 'ambulance'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
        : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300';

  return (
    <span className={`inline-flex shrink-0 whitespace-nowrap rounded-md px-2 py-0.5 text-[10px] font-semibold ${color}`}>
      {label}
    </span>
  );
}

function failureReason(log: WhatsAppDeliveryLog): string | null {
  const reason = String(log.channelFailureReason || log.errorMessage || '').trim();
  return reason || null;
}

function isDoctorRecipient(log: WhatsAppDeliveryLog) {
  const type = String(log.recipientType || '').toLowerCase();
  return type === 'vendor' || type === 'doctor' || Boolean(log.doctorName || log.metadata?.doctorName);
}

function FailureReasonDialog({
  log,
  reason,
  onClose,
}: {
  log: WhatsAppDeliveryLog;
  reason: string;
  onClose: () => void;
}) {
  const doctorLabel =
    log.doctorName ||
    log.metadata?.doctorName ||
    log.metadata?.vendorName ||
    (isDoctorRecipient(log) ? log.recipientName : null);

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Failure reason</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {log.templateName || 'Template'} · {new Date(log.createdAt).toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {doctorLabel ? (
            <div className="rounded-xl border border-purple-200 bg-purple-50/70 p-3 dark:border-purple-900 dark:bg-purple-950/30">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase text-purple-700 dark:text-purple-300">Doctor</p>
                <button
                  type="button"
                  onClick={() => void copy(doctorLabel, 'Doctor name')}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-700 hover:underline dark:text-purple-300"
                >
                  <Copy className="h-3 w-3" />
                  Copy
                </button>
              </div>
              <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{doctorLabel}</p>
              <p className="mt-1 font-mono text-xs text-gray-500">{log.maskedPhone}</p>
              {log.metadata?.clinicName ? (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{log.metadata.clinicName}</p>
              ) : null}
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
              <p className="text-xs font-medium uppercase text-gray-400">Recipient</p>
              <p className="mt-1 text-sm font-semibold">{log.recipientName || log.maskedPhone}</p>
              <p className="mt-1 font-mono text-xs text-gray-500">{log.maskedPhone}</p>
            </div>
          )}

          <div className="rounded-xl border border-red-200 bg-red-50/80 p-3 dark:border-red-900 dark:bg-red-950/30">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium uppercase text-red-700 dark:text-red-300">Error details</p>
              <button
                type="button"
                onClick={() => void copy(reason, 'Failure reason')}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 hover:underline dark:text-red-300"
              >
                <Copy className="h-3 w-3" />
                Copy
              </button>
            </div>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm text-red-800 dark:text-red-100">
              {reason}
            </p>
          </div>

          <dl className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <dt className="text-gray-400">Event</dt>
              <dd className="mt-0.5 font-mono text-gray-700 dark:text-gray-200">{log.eventType || '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-400">Source</dt>
              <dd className="mt-0.5 text-gray-700 dark:text-gray-200">
                {SOURCE_LABELS[log.source] || log.source}
              </dd>
            </div>
            <div>
              <dt className="text-gray-400">Status</dt>
              <dd className="mt-0.5">
                <StatusBadge status={log.status} />
              </dd>
            </div>
            <div>
              <dt className="text-gray-400">Message ID</dt>
              <dd className="mt-0.5 break-all font-mono text-gray-700 dark:text-gray-200">
                {log.interaktMessageId || '—'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={() => void copy(reason, 'Failure reason')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium dark:border-gray-600"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy reason
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const WhatsAppDeliveryPanel: React.FC = () => {
  const [logs, setLogs] = useState<WhatsAppDeliveryLog[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<WhatsAppDeliveryStatusCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [failureLog, setFailureLog] = useState<WhatsAppDeliveryLog | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchWhatsAppDeliveryLogs({
      source: source || undefined,
      status: status || undefined,
      days,
      search: search || undefined,
      limit: 100,
    })
      .then((r) => {
        setLogs(r.data.rows);
        setTotal(r.data.total);
        setStatusCounts(r.data.statusCounts || EMPTY_COUNTS);
      })
      .catch(() => {
        setLogs([]);
        setTotal(0);
        setStatusCounts(EMPTY_COUNTS);
      })
      .finally(() => setLoading(false));
  }, [source, status, days, search]);

  const filterClass =
    'w-full min-w-0 rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 sm:w-auto';

  const overviewCards: Array<{
    key: string;
    label: string;
    value: number;
    activeClass: string;
  }> = [
    {
      key: '',
      label: 'Total',
      value: statusCounts.total,
      activeClass: 'border-gray-900 ring-1 ring-gray-900 dark:border-white dark:ring-white',
    },
    {
      key: 'delivered',
      label: 'Delivered',
      value: statusCounts.delivered,
      activeClass: 'border-green-600 ring-1 ring-green-600',
    },
    {
      key: 'read',
      label: 'Read',
      value: statusCounts.read,
      activeClass: 'border-emerald-600 ring-1 ring-emerald-600',
    },
    {
      key: 'sent',
      label: 'Sent',
      value: statusCounts.sent,
      activeClass: 'border-blue-600 ring-1 ring-blue-600',
    },
    {
      key: 'failed',
      label: 'Failed',
      value: statusCounts.failed,
      activeClass: 'border-red-600 ring-1 ring-red-600',
    },
    {
      key: 'queued',
      label: 'Queued',
      value: statusCounts.queued,
      activeClass: 'border-gray-500 ring-1 ring-gray-500',
    },
  ];

  const failureDialogReason = failureLog ? failureReason(failureLog) : null;

  return (
    <WhatsAppPanelShell
      title="Delivery"
      description="Message delivery status from Interakt webhooks. Click overview cards to filter."
    >
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {overviewCards.map((card) => {
          const active = status === card.key;
          return (
            <button
              key={card.label}
              type="button"
              onClick={() => setStatus(card.key)}
              className={`rounded-xl border bg-white p-3 text-left transition dark:bg-gray-900 ${
                active
                  ? card.activeClass
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
              }`}
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{card.label}</p>
              <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{card.value}</p>
            </button>
          );
        })}
      </div>

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:gap-3">
        <input
          type="search"
          placeholder="Search template, event, recipient, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={filterClass}
        />
        <select value={source} onChange={(e) => setSource(e.target.value)} className={filterClass}>
          <option value="">All sources</option>
          <option value="transactional">Auto (backend)</option>
          <option value="ivr">IVR</option>
          <option value="campaign">Campaign</option>
          <option value="retargeting">Retargeting</option>
          <option value="inbox">Inbox</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={filterClass}>
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="read">Read</option>
          <option value="failed">Failed</option>
          <option value="queued">Queued</option>
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={filterClass}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-gray-500">Loading...</p>
      ) : logs.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">No delivery logs in this period</p>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            {logs.map((log) => {
              const reason = failureReason(log);
              const targetPhone = log.phoneNumber || log.maskedPhone;
              return (
                <div key={log.id} className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{log.templateName || '—'}</p>
                      <p className="font-mono text-[10px] text-gray-400">{log.eventType || '—'}</p>
                      <p className="text-xs text-gray-500">{SOURCE_LABELS[log.source] || log.source}</p>
                    </div>
                    <StatusBadge status={log.status} />
                  </div>

                  <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2 dark:border-gray-800">
                    <div>
                      <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                        {log.recipientName || log.maskedPhone}
                      </p>
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <RecipientTypeBadge recipientType={log.recipientType} />
                        <span className="text-[11px] text-gray-500">{log.maskedPhone}</span>
                      </div>
                    </div>
                    <Link
                      to={`/operations/whatsapp?phone=${encodeURIComponent(targetPhone || '')}`}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                      Chat
                    </Link>
                  </div>

                  <p className="mt-1.5 text-xs text-gray-400">{new Date(log.createdAt).toLocaleString()}</p>

                  {reason ? (
                    <button
                      type="button"
                      onClick={() => setFailureLog(log)}
                      className="mt-2 flex w-full items-center justify-between rounded-lg bg-red-50 px-3 py-2 text-left text-xs dark:bg-red-950/40"
                    >
                      <span className="line-clamp-1 text-red-700 dark:text-red-200">{reason}</span>
                      <span className="inline-flex shrink-0 items-center gap-1 font-semibold text-red-700 dark:text-red-300">
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </span>
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/80">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Time</th>
                  <th className="px-4 py-2 text-left font-medium">Source</th>
                  <th className="px-4 py-2 text-left font-medium">Event / Template</th>
                  <th className="px-4 py-2 text-left font-medium">Recipient</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Failure reason</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const reason = failureReason(log);
                  const targetPhone = log.phoneNumber || log.maskedPhone;
                  return (
                    <tr key={log.id} className="border-t border-gray-100 align-top dark:border-gray-800">
                      <td className="whitespace-nowrap px-4 py-2 text-gray-600">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">{SOURCE_LABELS[log.source] || log.source}</td>
                      <td className="px-4 py-2">
                        <div className="font-medium">{log.templateName || '—'}</div>
                        <div className="font-mono text-[10px] text-gray-400">{log.eventType || '—'}</div>
                      </td>
                      <td className="min-w-[200px] max-w-[260px] px-4 py-2">
                        <Link
                          to={`/operations/whatsapp?phone=${encodeURIComponent(targetPhone || '')}`}
                          className="group block hover:underline"
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-gray-900 group-hover:text-emerald-600 dark:text-gray-100">
                              {log.recipientName || log.maskedPhone}
                            </span>
                            <MessageSquare className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-emerald-600" />
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <RecipientTypeBadge recipientType={log.recipientType} />
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                              {log.maskedPhone}
                            </span>
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge status={log.status} />
                      </td>
                      <td className="max-w-[280px] px-4 py-2">
                        {reason ? (
                          <button
                            type="button"
                            onClick={() => setFailureLog(log)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-950/70"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!loading && total > logs.length && (
        <p className="mt-3 text-xs text-gray-500">
          Showing {logs.length} of {total} messages
        </p>
      )}

      {failureLog && failureDialogReason ? (
        <FailureReasonDialog
          log={failureLog}
          reason={failureDialogReason}
          onClose={() => setFailureLog(null)}
        />
      ) : null}
    </WhatsAppPanelShell>
  );
};

export default WhatsAppDeliveryPanel;
